import React, { useState, useEffect } from 'react';
import Modal from '../../components/common/Modal';
import * as XLSX from 'xlsx';
import { feesAPI } from "../../services/api";

const initialFormData = {
    academicYear: "",   // will hold the AcademicYear ID, not a label
    curriculum: "",
    feeName: "",
    feeCode: "",
    feeDescription: "",
    dueDates: [""],
};

const formatDueDate = (isoDate) => {
    if (!isoDate) return "";
    const d = new Date(isoDate + "T00:00:00");
    if (isNaN(d.getTime())) return isoDate;

    const day = d.getDate();
    const month = d.toLocaleDateString("en-US", { month: "long" });
    const suffix =
        day % 10 === 1 && day !== 11 ? "st" :
            day % 10 === 2 && day !== 12 ? "nd" :
                day % 10 === 3 && day !== 13 ? "rd" : "th";

    return `${month} ${day}${suffix}`;
};

const FeeType = () => {

    const [formData, setFormData] = useState(initialFormData);
    const [showViewModal, setShowViewModal] = useState(false);

    // index of the row being edited, or null when adding a new one
    const [editIndex, setEditIndex] = useState(null);

    const [feeTypeList, setFeeTypeList] = useState([]);
    const [academicYears, setAcademicYears] = useState([]); // [{id, name}]
    const [curriculums, setCurriculums] = useState([]);     // [{id, name}]
    const [loading, setLoading] = useState(false);

    const loadFeeTypes = async () => {
        try {
            setLoading(true);
            const res = await feesAPI.feeTypes();
            setFeeTypeList(res.data.results || res.data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const loadAcademicYears = async () => {
        try {
            // TODO: point this at whatever your real endpoint is
            // (e.g. feesAPI.academicYears() or a separate studentsAPI.academicYears())
            const res = await feesAPI.academicYears();
            setAcademicYears(res.data.results || res.data);
        } catch (err) {
            console.log(err);
        }
    };

    const loadCurriculums = async () => {
        try {
            // TODO: point this at your real endpoint once the Curriculum model exists.
            // Expected shape: [{id, name}], e.g. [{id: 1, name: "CBSE"}]
            //
            // If you go with "Option B" (no Curriculum model, just distinct strings
            // from FeeCategory) instead, swap this for something like:
            //   const res = await feesAPI.curriculumChoices(); // e.g. ["SSC","CBSE"]
            //   setCurriculums(res.data.map((name) => ({ id: name, name })));
            const res = await feesAPI.curriculums();
            setCurriculums(res.data.results || res.data);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        loadFeeTypes();
        //loadAcademicYears();
        //loadCurriculums();
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleDateChange = (index, value) => {
        const updatedDates = [...formData.dueDates];
        updatedDates[index] = value;

        setFormData({
            ...formData,
            dueDates: updatedDates,
        });
    };

    const addMoreDate = () => {
        setFormData({
            ...formData,
            dueDates: [...formData.dueDates, ""],
        });
    };

    const openAddModal = () => {
        setFormData(initialFormData);
        setEditIndex(null);
        setShowViewModal(true);
    };

    // Maps the ACTUAL shape returned by FeeCategorySerializer, not made-up camelCase fields
    const openEditModal = (index) => {
        const row = feeTypeList[index];

        setFormData({
            academicYear: row.academic_year ?? "",
            curriculum: row.curriculum ?? "",
            feeName: row.name ?? "",
            feeCode: row.code ?? "",
            feeDescription: row.description ?? "",
            dueDates: row.installments?.length
                ? row.installments.map((inst) => inst.due_date)
                : [""],
        });
        setEditIndex(index);
        setShowViewModal(true);
    };

    const handleDelete = async (index) => {
        if (!window.confirm("Delete this Fee Type?"))
            return;

        try {
            await feesAPI.deleteCategory(feeTypeList[index].id);
            loadFeeTypes();
        } catch (err) {
            console.log(err);
        }
    };

    const handleCancel = () => {
        setFormData(initialFormData);
        setEditIndex(null);
        setShowViewModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // due_dates is handled natively by FeeCategorySerializer.create()/update() -
            // no need to hit a separate installment endpoint.
			const payload = {
				academic_year: parseInt(formData.academicYear, 10),
				curriculum: parseInt(formData.curriculum, 10),
				name: formData.feeName,
				code: formData.feeCode,
				description: formData.feeDescription,
				due_dates: formData.dueDates.filter(Boolean),
			};

            if (editIndex !== null) {
                await feesAPI.updateFeeType(
                    feeTypeList[editIndex].id,
                    payload
                );
            } else {
                await feesAPI.createFeeType(payload);
            }

            await loadFeeTypes();

            setShowViewModal(false);
            setFormData(initialFormData);
            setEditIndex(null);
        } catch (err) {
            console.log(err);
        }
    };

    const exportToExcel = () => {
        const exportData = feeTypeList.map((row, index) => ({
            "S.No": index + 1,
            "Academic Year": row.academic_year_name,
            "Curriculum": row.curriculum_name ?? row.curriculum,
            "Fee Name": row.name,
            "Fee Code": row.code,
            "Fee Description": row.description,
            "Due Dates": (row.installments || [])
                .map((inst) => formatDueDate(inst.due_date))
                .join(", "),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Types");
        XLSX.writeFile(workbook, "fee-types.xlsx");
    };

    return (
        <div>
            <div className="page-header mb-3">
                <div><h2>List of Fee Types</h2></div>
                <button className="btn btn-primary" onClick={openAddModal}>➕ Add Fee Type</button>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive border">
                        <table className="table fee-table">
                            <thead>
                                <tr>
                                    <th>S.No </th>
                                    <th>Academic year </th>
                                    <th>curriculum </th>
                                    <th>Fee Name </th>
                                    <th>Fee Code </th>
                                    <th>Fee Description </th>
                                    <th>Installments and Due date </th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading && (
                                    <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                                )}
                                {!loading && feeTypeList.length === 0 && (
                                    <tr><td colSpan="8" className="text-center">No fee types found</td></tr>
                                )}
                                {feeTypeList.map((row, index) => (
                                    <tr key={row.id ?? index}>
                                        <td>{index + 1}</td>
                                        <td>{row.academic_year_name}</td>
                                        {/* If curriculum becomes a FK (Option A), swap this for
                                            row.curriculum_name once the serializer exposes it,
                                            same way academic_year_name works above. */}
                                        <td>{row.curriculum_name ?? row.curriculum}</td>
                                        <td>{row.name}</td>
                                        <td>{row.code}</td>
                                        <td>{row.description}</td>
                                        <td>
                                            {row.installments?.map((item) => (
                                                <div key={item.id}>
                                                    {formatDueDate(item.due_date)}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="text-center">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={() => openEditModal(index)}
                                            >
                                                <i className="bi bi-pencil"></i> Edit
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleDelete(index)}
                                            >
                                                <i className="bi bi-trash"></i> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <button className="export-btn" onClick={exportToExcel}>
                        Export to Excel
                    </button>

                    <div className="note-section">
                        <strong>Note :</strong>
                        <span>
                            {" "}
                            Fee type cannot edit or delete if any transactions made with same fee type
                        </span>
                    </div>

                </div>
            </div>

            <Modal
                show={showViewModal}
                onClose={handleCancel}
                title={editIndex !== null ? "Edit Fee Type" : "Add Fee Type"}
                size="md"
            >
                <div className="container-fluid">
                    <form onSubmit={handleSubmit}>
                        <div className='row'>
                            <div className="col-md-6 col-6 mb-2">
                                <label className="form-label">
                                    Select Academic Year
                                </label>

									<select
										className="form-select"
										name="academicYear"
										value={formData.academicYear}
										onChange={handleChange}
									>
										<option value="">Select Academic Year</option>
										<option value="1">2025-2026</option>
										<option value="2">2026-2027</option>
									</select>
                            </div>

                            <div className="col-md-6 col-6 mb-2">
                                <label className="form-label">
                                    Select Curriculum
                                </label>

								<select
									className="form-select"
									name="curriculum"
									value={formData.curriculum}
									onChange={handleChange}
								>
									<option value="">Select Curriculum</option>
									<option value="1">CBSE</option>
									<option value="2">SSC</option>
									<option value="3">ICSE</option>
								</select>
																							
								</div>

                            <div className="col-md-6 col-6 mb-2">
                                <label className="form-label">
                                    Fee Name
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    name="feeName"
                                    value={formData.feeName}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-md-6 col-6 mb-2">
                                <label className="form-label">
                                    Fee Code
                                </label>

                                <input
                                    type="text"
                                    className="form-control"
                                    name="feeCode"
                                    value={formData.feeCode}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="form-label">
                                Fee Description
                            </label>

                            <textarea
                                className="form-control"
                                rows="2"
                                name="feeDescription"
                                value={formData.feeDescription}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="mb-2">
                            <label className="form-label">
                                Select Terms and Due Dates
                            </label>

                            {formData.dueDates.map((date, index) => (
                                <div
                                    key={index}
                                    className="d-flex align-items-center gap-2 mb-2"
                                >
                                    <input
                                        type="date"
                                        className="form-control"
                                        style={{ maxWidth: "220px" }}
                                        value={date}
                                        onChange={(e) =>
                                            handleDateChange(index, e.target.value)
                                        }
                                    />

                                    {index === formData.dueDates.length - 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={addMoreDate}
                                        >
                                            Add More
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-4">
                            <button
                                type="submit"
                                className="btn btn-info text-white me-2 px-4"
                            >
                                {editIndex !== null ? "Update" : "Submit"}
                            </button>

                            <button
                                type="button"
                                className="btn btn-light border px-4"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    )
}

export default FeeType
